import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MessageCircle, BookOpen, Feather, Search, Check, ChevronDown } from "lucide-react";
import { useAuth } from "@/auth";
import AppleSignIn from "@/components/AppleSignIn";
import { Button, Field, cn } from "@/ui";
import { THEME_LIST, applyTheme } from "@/themes";
import { LEARNING_LEVELS } from "@/constants";

const TOUR = [
  { Icon: MessageCircle, title: "Everyday", body: "Ask about everyday stuff, ideas, questions, or anything you're trying to figure out." },
  { Icon: BookOpen, title: "Learn", body: "Get things explained, study, take quizzes, and work through schoolwork the right way." },
  { Icon: Feather, title: "Create", body: "Make stories, ideas, projects, inventions, characters, images — anything you can imagine." },
  { Icon: Search, title: "Research", body: "Find sources, check claims, compare information, and build citations with real evidence." },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const joinToken = params.get("join");
  const afterAuth = (acc) => joinToken ? `/social/join/${joinToken}` : (acc?.onboarding_complete ? "/home" : "/onboarding?step=tour");
  const { account, userRegister, userLogin, refresh, appleSignIn, updateSettings, guestStart, guestLogin, sessionLogin } = useAuth();
  const [step, setStep] = useState(params.get("step") === "tour" ? "tour" : "choice");
  const [name, setName] = useState("");
  const [level, setLevel] = useState("");
  const [levelOpen, setLevelOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [coupon, setCoupon] = useState("");
  const [appleMode, setAppleMode] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [ti, setTi] = useState(0);
  const [guestCode, setGuestCode] = useState("");
  const [testCode, setTestCode] = useState("");
  const [sessionCode, setSessionCode] = useState("");

  const shell = "min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-6";

  const onAppleUser = async (cred) => {
    setErr("");
    try {
      const data = await appleSignIn("user", cred);
      if (data.needs_onboarding) {
        setName(data.account?.name && data.account.name !== "there" ? data.account.name : "");
        setLevel(data.account?.learning_level || "");
        setAppleMode(true);
        setStep("setup");
      } else {
        await refresh();
        navigate(joinToken ? `/social/join/${joinToken}` : "/home");
      }
    } catch (e) {
      setErr(e.message);
    }
  };

  if (step === "choice") {
    return (
      <div className={shell}>
        <div className="w-full max-w-md flex flex-col gap-10">
          <div className="flex flex-col gap-3">
            <div className="flex items-center">
              <img src="/maxx-logo-onlight.png" alt="MAXx" className="h-16 w-auto dark:hidden" />
              <img src="/maxx-logo-ondark.png" alt="MAXx" className="hidden h-16 w-auto dark:block" />
            </div>
            <p className="text-lg text-muted-foreground">MAXx doesn't just answer questions — it helps you learn.</p>
          </div>
          <div className="flex flex-col gap-3">
            <Button data-testid="user-signin-btn" onClick={() => { setErr(""); setStep("login"); }}>Sign in</Button>
            <Button data-testid="add-parent-account" variant="secondary" onClick={() => navigate("/portal/parent")}>
              I'm a parent or guardian
            </Button>
            <Button data-testid="school-login-link" variant="secondary" onClick={() => navigate("/portal/school")}>
              School login
            </Button>
            <div className="flex items-center gap-3 py-1 text-xs text-muted-foreground">
              <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
            </div>
            <AppleSignIn onCredential={onAppleUser} onError={setErr} />
            {err ? <p data-testid="choice-error" className="text-center text-sm font-semibold">{err}</p> : null}
          </div>
          <div className="flex flex-col items-center gap-3">
            <button data-testid="create-account-link" className="text-sm font-semibold" onClick={() => { setAppleMode(false); setErr(""); setStep("setup"); }}>
              No user account? Create one here
            </button>
            <button data-testid="portal-link" className="text-sm text-muted-foreground" onClick={() => navigate("/portal/affiliate")}>
              Affiliate? Log in here
            </button>
            <button data-testid="test-access-link" className="text-sm text-muted-foreground" onClick={() => { setErr(""); setName(""); setLevel(""); setStep("test"); }}>
              Just testing? Use a test account
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "login") {
    const submit = async () => {
      setErr("");
      if (!email.includes("@") || !password) return setErr("Enter your email and password.");
      setLoading(true);
      try {
        const acc = await userLogin(email.trim(), password);
        navigate(afterAuth(acc));
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    };
    return (
      <div className={shell}>
        <div className="w-full max-w-md flex flex-col gap-5">
          <h1 className="text-3xl font-semibold tracking-tight">Welcome back</h1>
          <p className="text-sm text-muted-foreground">Log in with your email and password.</p>
          <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@email.com" data-testid="login-email" />
          <Field label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="login-password" />
          {err ? <p data-testid="login-error" className="text-sm font-semibold">{err}</p> : null}
          <Button data-testid="login-submit" onClick={submit} loading={loading}>Log in</Button>
          <button data-testid="forgot-password-link" className="text-center text-sm text-muted-foreground" onClick={() => navigate("/recover")}>
            Forgot your password?
          </button>
          <button data-testid="session-code-link" className="text-center text-sm text-muted-foreground" onClick={() => { setErr(""); setSessionCode(""); setName(""); setStep("sessioncode"); }}>
            Session Code? Enter it here
          </button>
          <button data-testid="test-code-link" className="text-center text-sm text-muted-foreground" onClick={() => { setErr(""); setTestCode(""); setStep("testcode"); }}>
            Have a test code? Enter it
          </button>
          <button className="text-center text-sm text-muted-foreground" onClick={() => { setErr(""); setStep("choice"); }}>Back</button>
        </div>
      </div>
    );
  }

  if (step === "setup") {
    const submit = async () => {
      setErr("");
      if (!name.trim()) return setErr("Please enter your name.");
      if (!level) return setErr("Please choose a learning level.");
      if (!appleMode) {
        if (!email.includes("@")) return setErr("Enter a valid email.");
        if (password.length < 6) return setErr("Password must be at least 6 characters.");
      }
      setLoading(true);
      try {
        if (appleMode) {
          await updateSettings({ name: name.trim(), learning_level: level });
        } else {
          await userRegister({
            name: name.trim(), learning_level: level, email: email.trim(),
            password, coupon_code: coupon.trim() || undefined,
          });
        }
        setStep("tour");
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    };
    return (
      <div className={shell}>
        <div className="w-full max-w-md flex flex-col gap-5">
          <h1 className="text-3xl font-semibold tracking-tight">Set up MAXx</h1>
          <p className="text-sm text-muted-foreground">{appleMode ? "Just two things and you're in." : "Create your login so you can come back anytime."}</p>
          <Field label="What should MAXx call you?" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="setup-name" />
          <div className="relative flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-muted-foreground">Learning Level</span>
            <button
              data-testid="level-picker"
              onClick={() => setLevelOpen((o) => !o)}
              className="flex items-center justify-between rounded-xl border border-input bg-background px-4 py-3 text-[15px]"
            >
              <span className={level ? "" : "text-muted-foreground"}>{level || "Choose a level"}</span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>
            {levelOpen ? (
              <div className="absolute top-full z-10 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-border bg-popover p-1 max-scroll shadow-lg">
                {LEARNING_LEVELS.map((l) => (
                  <button
                    key={l}
                    data-testid={`level-${l}`}
                    onClick={() => { setLevel(l); setLevelOpen(false); }}
                    className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-[15px] hover:bg-accent"
                  >
                    {l} {level === l ? <Check className="h-4 w-4" /> : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          {!appleMode ? (
            <>
              <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@email.com" data-testid="setup-email" />
              <Field label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" data-testid="setup-password" />
              <Field label="Coupon code (optional)" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="e.g. MAX20" data-testid="setup-coupon" />
            </>
          ) : null}
          {err ? <p data-testid="setup-error" className="text-sm font-semibold">{err}</p> : null}
          <Button data-testid="setup-continue" onClick={submit} loading={loading}>Continue</Button>
          <button className="text-center text-sm text-muted-foreground" onClick={() => { setErr(""); setStep("choice"); }}>Back</button>
        </div>
      </div>
    );
  }

  if (step === "test") {
    const submit = async () => {
      setErr("");
      if (!name.trim()) return setErr("Please enter your name.");
      setLoading(true);
      try {
        const data = await guestStart(name.trim(), level || undefined);
        setGuestCode(data.guest_code);
        setStep("testdone");
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    };
    return (
      <div className={shell}>
        <div className="w-full max-w-md flex flex-col gap-5">
          <h1 className="text-3xl font-semibold tracking-tight">Try MAXx — no email</h1>
          <p className="text-sm text-muted-foreground">Just a name to start. We'll give you a code so you can come back later.</p>
          <Field label="What should MAXx call you?" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="test-name" />
          <div className="relative flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-muted-foreground">Learning Level (optional)</span>
            <button data-testid="test-level-picker" onClick={() => setLevelOpen((o) => !o)} className="flex items-center justify-between rounded-xl border border-input bg-background px-4 py-3 text-[15px]">
              <span className={level ? "" : "text-muted-foreground"}>{level || "Choose a level"}</span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>
            {levelOpen ? (
              <div className="absolute top-full z-10 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-border bg-popover p-1 max-scroll shadow-lg">
                {LEARNING_LEVELS.map((l) => (
                  <button key={l} data-testid={`test-level-${l}`} onClick={() => { setLevel(l); setLevelOpen(false); }} className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-[15px] hover:bg-accent">
                    {l} {level === l ? <Check className="h-4 w-4" /> : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          {err ? <p data-testid="test-error" className="text-sm font-semibold">{err}</p> : null}
          <Button data-testid="test-continue" onClick={submit} loading={loading}>Start</Button>
          <button className="text-center text-sm text-muted-foreground" onClick={() => { setErr(""); setStep("choice"); }}>Back</button>
        </div>
      </div>
    );
  }

  if (step === "testdone") {
    return (
      <div className={shell}>
        <div className="w-full max-w-md flex flex-col gap-6 text-center">
          <h1 className="text-3xl font-semibold tracking-tight">You're in! 🎉</h1>
          <p className="text-sm text-muted-foreground">This is your test code. Keep it to log back in later on any device — no email needed.</p>
          <div data-testid="test-code-value" className="rounded-2xl border border-border bg-secondary px-6 py-5 text-3xl font-bold tracking-[0.2em]">{guestCode}</div>
          <Button data-testid="test-start" onClick={() => { setTi(0); setStep("tour"); }}>Start using MAXx</Button>
        </div>
      </div>
    );
  }

  if (step === "testcode") {
    const submit = async () => {
      setErr("");
      if (!testCode.trim()) return setErr("Enter your test code.");
      setLoading(true);
      try {
        const acc = await guestLogin(testCode.trim());
        navigate(afterAuth(acc));
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    };
    return (
      <div className={shell}>
        <div className="w-full max-w-md flex flex-col gap-5">
          <h1 className="text-3xl font-semibold tracking-tight">Enter your test code</h1>
          <p className="text-sm text-muted-foreground">No email needed — just the code you got when you started.</p>
          <Field label="Test code" value={testCode} onChange={(e) => setTestCode(e.target.value)} placeholder="MAX-XXXXX" data-testid="testcode-input" />
          {err ? <p data-testid="testcode-error" className="text-sm font-semibold">{err}</p> : null}
          <Button data-testid="testcode-submit" onClick={submit} loading={loading}>Log in</Button>
          <button className="text-center text-sm text-muted-foreground" onClick={() => { setErr(""); setStep("login"); }}>Back</button>
        </div>
      </div>
    );
  }

  if (step === "sessioncode") {
    const submit = async () => {
      setErr("");
      if (!sessionCode.trim()) return setErr("Enter your session code.");
      if (!name.trim()) return setErr("Enter your name.");
      setLoading(true);
      try {
        const acc = await sessionLogin(sessionCode.trim(), name.trim());
        navigate(afterAuth(acc));
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    };
    return (
      <div className={shell}>
        <div className="w-full max-w-md flex flex-col gap-5">
          <h1 className="text-3xl font-semibold tracking-tight">Enter your session code</h1>
          <p className="text-sm text-muted-foreground">Type the code your teacher gave you and your name. This signs you in for class — no email or password needed.</p>
          <Field label="Session code" value={sessionCode} onChange={(e) => setSessionCode(e.target.value.toUpperCase())} placeholder="ABC123" data-testid="session-code-input" />
          <Field label="Your name" value={name} onChange={(e) => setName(e.target.value)} placeholder="First name + last initial" data-testid="session-name-input" />
          {err ? <p data-testid="session-error" className="text-sm font-semibold">{err}</p> : null}
          <Button data-testid="session-submit" onClick={submit} loading={loading}>Join class</Button>
          <button className="text-center text-sm text-muted-foreground" onClick={() => { setErr(""); setStep("login"); }}>Back</button>
        </div>
      </div>
    );
  }

  // tour
  const pickColor = (id) => { applyTheme(id); updateSettings({ color_theme: id }).catch(() => {}); };
  const isThemeStep = ti === TOUR.length;
  const finish = async () => {
    if (ti < TOUR.length) return setTi(ti + 1);
    setLoading(true);
    try {
      const { apiFetch } = await import("@/api");
      await apiFetch("/user/onboarding-complete", { method: "POST" });
      await refresh();
    } catch {}
    navigate(joinToken ? `/social/join/${joinToken}` : "/home");
  };
  const S = TOUR[ti];
  const currentTheme = account?.settings?.color_theme || "ocean";
  return (
    <div className={shell}>
      <div className="w-full max-w-md flex flex-1 flex-col items-center justify-center gap-8 py-16">
        {isThemeStep ? (
          <div className="flex w-full flex-col items-center gap-6">
            <div className="flex flex-col items-center gap-3 text-center">
              <h1 className="font-display text-4xl font-bold tracking-tight">Pick your color</h1>
              <p className="max-w-sm text-lg text-muted-foreground">Make MAXx yours. You can change it anytime in Settings.</p>
            </div>
            <div className="grid grid-cols-4 gap-4">
              {THEME_LIST.map((t) => {
                const selected = currentTheme === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`onboard-theme-${t.id}`}
                    onClick={() => pickColor(t.id)}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <span
                      className="flex h-14 w-14 items-center justify-center rounded-full transition-all"
                      style={{ background: t.bg, border: selected ? `3px solid ${t.accent}` : "1px solid hsl(var(--border))" }}
                    >
                      <span className="h-6 w-6 rounded-full" style={{ background: t.accent }} />
                    </span>
                    <span className="text-[11px] font-medium">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <>
            <div className="flex h-24 w-24 items-center justify-center rounded-3xl bg-secondary">
              <S.Icon className="h-10 w-10" strokeWidth={1.5} />
            </div>
            <div className="flex flex-col items-center gap-3 text-center">
              <h1 className="text-4xl font-bold tracking-tight">{S.title}</h1>
              <p className="max-w-sm text-lg text-muted-foreground">{S.body}</p>
            </div>
          </>
        )}
        <div className="flex gap-2">
          {Array.from({ length: TOUR.length + 1 }).map((_, i) => (
            <span key={i} className={cn("h-2 rounded-full transition-all", i === ti ? "w-6 bg-primary" : "w-2 bg-border")} />
          ))}
        </div>
      </div>
      <div className="w-full max-w-md pb-10">
        <Button data-testid="tour-next" className="w-full" onClick={finish} loading={loading}>
          {ti < TOUR.length ? "Next" : "Start using MAXx"}
        </Button>
      </div>
    </div>
  );
}
